import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { SubscriptionsManager } from '../../helpers/subscriptions-manager';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() config!: any[];
  @Input() initialValues: any;
  form!: FormGroup;

  @Output() formSubmitted: EventEmitter<any> = new EventEmitter<any>();
  @Output() formChanges: EventEmitter<any> = new EventEmitter<any>();
  private subsManager = new SubscriptionsManager();

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.form = this.createGroup(this.config);
    this.applyInitialValues();
    this.subscribeToChanges();
    this.subscribeToColorPickerChanges();
    this.cdr.detectChanges();
  }
  applyInitialValues() {
    if (this.initialValues) {
      Object.keys(this.initialValues).forEach(key => {
        const control = this.form.get(key);
        const controlConfig = this.config.find(ctrl => ctrl.name === key);

        if (control && controlConfig) {
          let value = this.initialValues[key];

          if (controlConfig.type === 'multi-select') {

            if (Array.isArray(value)) {

            } else if (typeof value === 'string') {
              if (value.includes(',')) {
                value = value.split(',').map(item => item.trim());
              } else if (value.trim() !== '') {
                value = [value.trim()];
              } else {
                value = [];
              }
            } else {
              value = [];
            }
          }
          control.setValue(value);
        }
      });
      this.form.patchValue(this.initialValues);
      this.updateColorPickers();
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config'] && changes['config'].currentValue) {
      this.form = this.createGroup(this.config);
      this.applyInitialValues();
      this.subscribeToChanges();
      this.subscribeToColorPickerChanges();
    }

    if (changes['initialValues'] && !changes['initialValues'].isFirstChange()) {
      this.form.patchValue(this.initialValues || {});
      this.updateColorPickers();
    }
  }

  ngOnDestroy(): void {
    this.subsManager.unsubscribeAll();
  }

  createGroup(config: any[]): FormGroup {
    const group = new FormGroup({});
    config.forEach((control) => {
      let initialValue;

      if (control.type === 'multi-select' || control.type === 'multi-select-chip') {
        initialValue = Array.isArray(control.initialValue)
          ? control.initialValue
          : control.initialValue ? [control.initialValue] : [];
      } else if (control.type === 'checkbox') {
        initialValue = false;
      } else {
        initialValue = '';
      }

      group.addControl(
        control.name,
        new FormControl(initialValue, this.mapValidators(control.validators, control.type))
      );
    });
    return group;
  }

  onMultiSelectChange(selectedValues: any, controlName: string) {
    const control = this.form.get(controlName);
    if (control) {
      if (Array.isArray(selectedValues)) {
        control.setValue(selectedValues);
      } else if (typeof selectedValues === 'string') {
        control.setValue(selectedValues.split(',').map(item => item.trim()));
      } else {
        control.setValue([]);
      }
    }
  }
  onMultiSelectChipChange(selectedValues: any, controlName: string) {
    const control = this.form.get(controlName);
    if (control) {
      control.setValue(selectedValues);
      this.cdr.markForCheck();
    }

  }

  onJsonBuilderChange(event: any, controlName: string) {
    const control = this.form.get(controlName);
    if (control && event !== control.value) {
      control.setValue(event, { emitEvent: false });
      this.formChanges.emit(this.form.value);
    }
  }

  getSelectedValues(controlName: string): any[] {
    const control = this.form.get(controlName);
    return control ? control.value || [] : [];
  }

  subscribeToChanges() {
    this.subsManager.add(
      this.form.valueChanges.subscribe((values) => {
        this.formChanges.emit(values);
      })
    );
  }

  subscribeToColorPickerChanges() {
    this.config.forEach((control) => {
      if (control.type === 'color-picker') {
        const controlFormControl = this.form.get(control.name);
        if (controlFormControl) {
          this.subsManager.add(
            controlFormControl.valueChanges.subscribe((value) => {
              const colorPicker = document.getElementById(`${control.name}-color-picker`) as HTMLInputElement;
              if (colorPicker && this.isValidHex(value)) {
                colorPicker.value = value;
              }
            })
          );
        }
      }
    });
  }

  updateColorPickers() {
    this.config.forEach((control) => {
      if (control.type === 'color-picker') {
        const value = this.form.get(control.name)?.value;
        const colorPicker = document.getElementById(`${control.name}-color-picker`) as HTMLInputElement;
        if (colorPicker && this.isValidHex(value)) {
          colorPicker.value = value;
        }
      }
    });
  }

  mapValidators(validators: any, controlType: string): ValidatorFn[] {
    const formValidators: ValidatorFn[] = [];
    if (validators?.required) {
      formValidators.push(Validators.required);
    }
    if (validators?.min) {
      formValidators.push(Validators.min(validators.min));
    }
    if (controlType === 'color-picker') {
      formValidators.push(this.hexColorValidator());
    }
    return formValidators;
  }

  onSubmit() {
    if (this.form.valid) {
      this.formSubmitted.emit(this.form.value);
    } else {
      this.form.markAllAsTouched();
    }
  }

  getWidthClass(width: string): string {
    switch (width) {
      case 'full':
        return 'w-full';
      case 'half':
        return 'w-1/2';
      case '3/4':
        return 'w-3/4';
      case '1/4':
        return 'w-1/4';
      default:
        return 'w-full';
    }
  }

  hexColorValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any; } | null => {
      const isValid = this.isValidHex(control.value);
      return isValid ? null : { hexColor: { value: control.value } };
    };
  }

  isValidHex(value: string): boolean {
    return /^#([0-9A-F]{3}){1,2}$/i.test(value);
  }

  onColorPickerChange(event: Event, controlName: string) {
    const color = (event.target as HTMLInputElement).value;
    this.form.get(controlName)?.setValue(color);
  }

  onTextInputChange(event: Event, controlName: string) {
    const value = (event.target as HTMLInputElement).value;
    if (this.isValidHex(value)) {
      const colorPicker = document.getElementById(`${controlName}-color-picker`) as HTMLInputElement;
      if (colorPicker) {
        colorPicker.value = value;
      }
    }
  }
  // jsonText = '';
  // isInvalidJson = false;

  onJsonInput(event: Event, controlName: string) {
    const control = this.form.get(controlName);
    const text = control?.value;

    try {
      JSON.parse(text);
      control?.setErrors(null);
      this.updateHighlightedJson(controlName);
    } catch (error) {
      control?.setErrors({ invalidJson: true });
    }
  }

  updateHighlightedJson(controlName: string) {
    const outputElement = document.querySelector('.json-editor-output') as HTMLElement;
    const control = this.form.get(controlName);
    outputElement.innerHTML = this.syntaxHighlight(control?.value || '');
  }

  syntaxHighlight(json: string) {
    return json.replace(
      /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|\b\d+\.?\d*\b)/g,
      (match) => {
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            return `<span class="json-key">${match}</span>`;
          } else {
            return `<span class="json-string">${match}</span>`;
          }
        } else if (/true|false/.test(match)) {
          return `<span class="json-boolean">${match}</span>`;
        } else if (/null/.test(match)) {
          return `<span class="json-null">${match}</span>`;
        } else {
          return `<span class="json-number">${match}</span>`;
        }
      }
    );
  }

  validateJson(controlName: string) {
    const control = this.form.get(controlName);
    try {
      JSON.parse(control?.value);
      control?.setErrors(null);
    } catch (e) {
      control?.setErrors({ invalidJson: true });
    }
  }
}
